import { useState } from "react";

import axios from "axios";
import { useHistory } from "react-router-dom";

import { baseUrl, conf, msgs } from "@config/conf";
import { useAuth } from "@contexts/AuthContext";
import { useStore } from "react-redux";
import {
  cacheBegin,
  cacheClear,
  cacheSuccess,
  cacheFailure,
  cacheClearPrefix,
} from "@redux/actions/cachedApiActions";

import useToast from "./useToast";
//import store from "@containers/App/store";
import { selectCacheEntry, isFresh } from "@redux/reducers/cachedApiReducer";

const { authError, error, annuityAllocationAuthError } = msgs || {};

const CACHE_VERSION = "v1"; // bump on deploy to invalidate all cached keys

function stableParams(params) {
  if (!params) return "";
  const entries = Object.entries(params).sort(([a], [b]) => (a > b ? 1 : -1));
  return new URLSearchParams(Object.fromEntries(entries)).toString();
}
function makeKey({ url, params, cacheKey }) {
  return `${CACHE_VERSION}:${cacheKey || `${url}?${stableParams(params)}`}`;
}
function genId() {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

const useApiFetch = () => {
  const { token, logout, annuityToken, annuityLogout, rkcToken } = useAuth();

  const history = useHistory();
  const { showErrorToast, showSuccessToast } = useToast();
  const [isPending, setIsPending] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const store = useStore();

  const path = window.location.pathname;
  const isViewAccountSummary = path.includes("/View/Account/Summary");
  const isViewGenericUserAccountSummary = path.includes(
    "/View/GenericUserAccount/Summary"
  );
  const isViewAccountDocuments = path.includes("View/Account/Documents");
  const isViewAccountUpdateAllocation = path.includes(
    "/View/Account/UpdateAnnuityAllocations"
  );
  const isIncomeActivation = path.includes("/View/Account/IncomeActivation");

  const authToken = isViewGenericUserAccountSummary
    ? annuityToken
    : isIncomeActivation
      ? rkcToken
      : token || annuityToken;
  const handleUnauthorizedError = () => {
    if (
      isViewAccountSummary ||
      isViewAccountDocuments ||
      isViewGenericUserAccountSummary ||
      isViewAccountUpdateAllocation ||
      isIncomeActivation
    ) {
      sessionStorage.setItem("validToken", false);
      sessionStorage.removeItem("annuityAuthToken");
      sessionStorage.removeItem("rkcToken");
      annuityLogout();
    } else if (isViewAccountUpdateAllocation) {
      setErrorMessage(annuityAllocationAuthError);
    } else {
      logout();
      showErrorToast(authError);
      history.push("/");
    }
  };

  function waitForKey(key) {
    return new Promise((resolve, reject) => {
      const unsubscribe = store.subscribe(() => {
        const entry = selectCacheEntry(store.getState(), key);
        if (!entry) return;
        if (entry.status === "succeeded") {
          unsubscribe();
          resolve(entry.data);
        }
        if (entry.status === "failed") {
          unsubscribe();
          reject(entry.error || "Request failed");
        }
      });
    });
  }

  const request = async (method, url, values, options) => {
    const {
      redirectUrl,
      successToast = true,
      loginAuth = false,
      successMessage = "",
      closeWindow = false,
    } = options || {};

    if (loginAuth ? true : authToken) {
      try {
        setIsPending(true);
        const response = await axios({
          method,
          url: `${conf.serverURL}${url}`,
          data: values,
          headers: {
            "Content-Type": "application/json",
            accessToken: authToken,
          },
        });
        if (response.status === 200) {
          if (successToast) {
            showSuccessToast(successMessage);
          }
          if (redirectUrl) {
            history.push(redirectUrl);
          }
          if (closeWindow) {
            setTimeout(() => {
              window.close();
            }, 3000);
          }
        }
        setIsPending(false);
        return response;
      } catch (err) {
        setIsPending(false);
        if (err?.response?.status === 401) {
          loginAuth
            ? showErrorToast(err?.response?.data?.message)
            : handleUnauthorizedError();
        } else {
          showErrorToast(err?.response?.data?.message || error);
        }
      }
    }
  };

  const post = (url, values, options) => request("post", url, values, options);
  const put = (url, values, options) => request("put", url, values, options);
  const deleteRequest = (url, values, options) =>
    request("delete", url, values, options);

  const get = async (url, options) => {
    const {
      errorToast = true,
      queryParams,
      shouldUseBaseUrl = false,
    } = options || {};
    const finalUrl = shouldUseBaseUrl ? baseUrl : conf.serverURL;

    if (authToken) {
      try {
        setIsPending(true);
        const response = await axios({
          method: "get",
          url: `${finalUrl}${url}`,
          params: queryParams,
          headers: {
            "Content-Type": "application/json",
            accessToken: authToken,
          },
        });
        if (response.status === 200) {
          setIsPending(false);
          return response?.data;
        }
      } catch (err) {
        setIsPending(false);
        setErrorMessage(err?.response?.data?.message || error);
        if (err?.response?.status === 401) {
          handleUnauthorizedError();
        } else if (errorToast) {
          showErrorToast(err?.response?.data?.message || error);
        }
      }
    }
  };

  const getCached = async (url, options = {}) => {
    const {
      params,
      headers,
      cacheKey, // e.g., 'masters' (recommended)
      cache = false, // true => use url+params as key if cacheKey not given
      ttlMs = 24 * 60 * 60 * 1000,
      force = false, // bypass TTL and refetch
      errorToast = true,
      queryParams,
      shouldUseBaseUrl = false, // axios options (signal, etc.)
    } = options;

    // if not opting into cache, behave like plain GET
    if (!cacheKey && !cache) return get(url, options);

    // Do not let cached reads bypass the authentication guard in get(). This
    // prevents a new tab from converting token hydration into a 401/logout.
    if (!authToken) return undefined;

    const key = makeKey({ url, params, cacheKey });
    const entry = selectCacheEntry(store.getState(), key, "cachedApi");

    // serve fresh cache immediately (no spinner flicker)
    if (!force && isFresh(entry)) return entry.data;

    // if another fetch is in-flight for this key, wait for it (dedupe)
    if (!force && entry?.status === "loading") {
      return waitForKey(key);
    }

    // we initiate the fetch
    setIsPending(true);
    const requestId = genId();
    store.dispatch(cacheBegin(key, requestId, ttlMs));
    const finalUrl = conf.serverURL;
    try {
      const response = await axios({
        method: "get",
        url: `${finalUrl}${url}`,
        params: queryParams,
        headers: {
          "Content-Type": "application/json",
          accessToken: authToken,
        },
      });
      if (response.status === 200) {
        setIsPending(false);
        store.dispatch(cacheSuccess(key, requestId, response?.data));
        return response?.data;
      }
    } catch (err) {
      setIsPending(false);
      setErrorMessage(err?.response?.data?.message || error);
      store.dispatch(
        cacheFailure(key, requestId, err?.response?.data?.message)
      );
      if (err?.response?.status === 401) {
        handleUnauthorizedError();
      } else if (errorToast) {
        showErrorToast(err?.response?.data?.message || error);
      }
    } finally {
      setIsPending(false);
    }
  };

  const invalidateCache = (keyOrPrefix) => {
    store.dispatch(cacheClearPrefix(`${CACHE_VERSION}:${keyOrPrefix}`));
  };
  const clearCacheKey = (key) => {
    store.dispatch(cacheClear(`${CACHE_VERSION}:${key}`));
  };

  return {
    post,
    get,
    put,
    deleteRequest,
    isPending,
    errorMessage,
    getCached,
    invalidateCache,
    clearCacheKey,
  };
};

export default useApiFetch;
